<template lang="pug">
el-form(
  ref="refForm"
  :rules="rules"
  :model="item"
  status-icon
  label-position="top"
).c-interest-detail-form
  app-main-content-layout(
    title='Interests'
    back-to='/manage-content/directory/interests'
    v-loading="loading"
  )
    template(#actions)
      .d-flex.align-items-center.gap-2
        .favorite-toggle(@click.stop="handleToggleAddToFav")
          fai(v-if="item.favorite" type="fas" icon="star").size-24.star-active
          fai(v-else type="fal" icon="star").size-24.star-inactive

        el-button(type="danger" plain @click="removeInterestItem").has-fai.min-w-110
          fai(type="fal" icon="trash").size-18
          span Delete

        el-button(type="primary" @click="validateForm" v-loading="loading").has-fai.min-w-110
          fai(type="fal" icon="save").size-18
          span Save

    template(#col-left)
      .c-content-section
        el-form-item(label="Interest" prop="interest")
          el-input(v-model="item.interest")

        el-form-item(label="Category", prop="categoryId").mb-0
          VueMultiselect(
            v-model="item.categoryId",
            :multiple="false",
            label="category",
            track-by="id",
            :options="categories.categories",
            :searchable="true",
            :close-on-select="true",
            :show-labels="false"
            placeholder="Pick a value"
            )

    template(#col-right)
      .c-content-section
        el-form-item(label="Creator")
          el-input(v-model="item.creator" disabled)

        el-form-item
          template(#label)
            | Post Date
            span.text-muted.ms-2 Active for 3 Days
          el-input(v-model="item.postDate" disabled)

        el-form-item(label="Post Time").mb-0
          el-input(v-model="item.postTime" disabled)
</template>

<style lang="scss" scoped>
// Color Variables - Match OrgEdit.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;
$color-star-active: #fbbf24;
$color-star-inactive: #d1d5db;

// Main Form Container
.c-interest-detail-form {
  padding: 2rem;

  :deep(.grid-2-cols) {
    gap: 1.5rem;
  }

  :deep(.col-has-child) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

// White Content Sections
.c-content-section {
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

// Favorite Toggle
.favorite-toggle {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background-color: $gray-100;
  }

  &:active {
    transform: scale(0.95);
  }

  .star-active {
    color: $color-star-active;
  }

  .star-inactive {
    color: $color-star-inactive;

    &:hover {
      color: color.adjust($color-star-inactive, $lightness: -10%);
    }
  }
}

.size-24 {
  font-size: 24px;
  width: 24px;
  height: 24px;
}

// Gap utility
.gap-2 {
  gap: 0.5rem;
}

// Form Styling
:deep(.el-form-item) {
  margin-bottom: 1.5rem;

  .el-form-item__label {
    color: $gray-700;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .el-input__wrapper {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 0 12px;
    height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover:not(.is-disabled) {
      border-color: #c0c4cc;
      box-shadow: none;
    }

    &.is-focus {
      border-color: #01beb0;
      box-shadow: none;
    }

    &.is-disabled {
      border: 1.5px solid $gray-200;
      background: #F0F0F3;
    }
  }

  .el-input__inner {
    height: 100%;
    font-size: 0.875rem;
    color: $gray-700;
    background: transparent;
    font-weight: 500;

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }

  .el-textarea__inner {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 12px;
    color: $gray-700;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    min-height: 100px;

    &:hover {
      border-color: #c0c4cc;
      box-shadow: none;
    }

    &:focus {
      border-color: #01beb0;
      box-shadow: none;
    }

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }
}

// Button Styling
:deep(.el-button) {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  height: 36px;
  padding: 0 20px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-width: 1px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem;

  > span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .fai,
  svg {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-right: 0.5rem;
  }

  &.min-w-110 {
    min-width: 110px;
  }

  &.el-button--primary {
    background-color: $color-purple-text;
    border-color: $color-purple-text;
    color: white;

    &:hover,
    &:focus {
      background-color: color.adjust($color-purple-text, $lightness: -8%);
      border-color: color.adjust($color-purple-text, $lightness: -8%);
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      background-color: color.adjust($color-purple-text, $lightness: -10%);
      border-color: color.adjust($color-purple-text, $lightness: -10%);
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(67, 73, 221, 0.2);
    }
  }

  &.el-button--danger {
    &.is-plain {
      background-color: white;
      border-color: #f56c6c;
      color: #f56c6c;

      &:hover {
        background-color: #fef0f0;
        border-color: #f56c6c;
        color: #f56c6c;
        box-shadow: 0 2px 4px 0 rgba(245, 108, 108, 0.2);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  &.el-button--default {
    background-color: white;
    border-color: $gray-200;
    color: $gray-700;

    &:hover {
      background-color: $gray-50;
      border-color: $gray-300;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.el-button--small {
    padding: 0 12px;
    font-size: 0.8125rem;
    height: 32px;
  }
}

// Select/Multiselect Styling
:deep(.multiselect) {
  .multiselect__tags {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 8px 12px;
    min-height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      border-color: #c0c4cc;
      box-shadow: none;
    }
  }

  &.multiselect--active .multiselect__tags {
    border-color: #01beb0;
    box-shadow: none;
  }

  .multiselect__single,
  .multiselect__input {
    color: $gray-700;
    font-weight: 500;
    font-size: 0.875rem;
    background: transparent;
    line-height: 24px;
    padding: 0;
    margin: 0;
    display: flex;
    align-items: center;
  }

  .multiselect__placeholder {
    color: $gray-400;
    font-size: 0.875rem;
    font-weight: 400;
  }

  .multiselect__content-wrapper {
    border-radius: 12px;
    border: 1.5px solid $gray-200;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
    margin-top: 6px;
  }

  .multiselect__option {
    border-radius: 8px;
    padding: 10px 12px;
    margin: 2px 6px;
    font-size: 0.9375rem;
    color: $gray-700;
    font-weight: 500;
    transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);

    &--highlight {
      background-color: rgba(67, 73, 221, 0.08);
      color: $color-purple-text;
      transform: translateX(2px);
    }

    &--selected {
      background-color: rgba(67, 73, 221, 0.12);
      color: $color-purple-text;
      font-weight: 600;
    }
  }
}

// Page Background
:deep(.app-main-content-layout) {
  background-color: $gray-50;
}

// Text Muted
.text-muted {
  color: $gray-500;
  font-size: 0.75rem;
  font-weight: 400;
}

// Responsive Design
@media (max-width: 768px) {
  .c-interest-detail-form {
    padding: 1.5rem;
  }

  .c-content-section {
    padding: 1.25rem;
    border-radius: 12px;
  }

  :deep(.grid-2-cols) {
    gap: 1rem;
  }

  :deep(.el-form-item) {
    margin-bottom: 1.25rem;

    .el-form-item__label {
      font-size: 0.8125rem;
    }

    .el-input__inner,
    .el-textarea__inner {
      font-size: 0.8125rem;
    }
  }

  :deep(.el-button) {
    padding: 8px 16px;
    font-size: 0.8125rem;
  }
}

@media (max-width: 576px) {
  .c-interest-detail-form {
    padding: 1rem;
  }

  .c-content-section {
    padding: 1rem;
    border-radius: 12px;
  }

  .d-flex.gap-2 {
    flex-wrap: wrap;
  }
}
</style>

<script lang="ts">
import {
  defineComponent,
  reactive,
  ref,
  onMounted,
} from 'vue';
import VueMultiselect from 'vue-multiselect';
import { requiredRule, category } from '@/data/rules';
import shared from './shared';

export default defineComponent({
  name: 'ManageContent_Directory_InterestDeail',
  components: { VueMultiselect },
  setup() {
    const {
      route,
      router,
      loading,
      getTypedCategories,
      getInterest,
      saveInterest,
      removeItemGetConfirm,
      dateAndTime,
    } = shared();
    const refForm = ref<any>(null);
    const { id } = route.params;
    const categories = reactive({ categories: [] });
    const item = reactive({
      id: -1,
      name: 'Very Long Interest Name',
      category: {

      },
      favorite: false,
      postDate: '02/12/2021',
      postTime: '2:30 PM',
    });

    async function getItem() {
      loading.value = true;
      const itm = await getInterest(parseInt(id, 10));
      item.id = itm.id;
      item.creator = itm.createdBy;
      item.categoryId = itm.category;
      ['interest', 'description', 'favorite'].forEach((attr) => {
        item[attr] = itm[attr];
      });
      [item.postDate, item.postTime] = dateAndTime(itm.createdAt);
      loading.value = false;
    }

    async function saveItem() {
      await saveInterest({
        id: parseInt(item.id, 10),
        interest: item.interest,
        description: item.description,
        categoryId: parseInt(item.categoryId.id, 10),
      });
    }

    function validateForm() {
      refForm.value.validate(async (valid) => {
        if (valid) await saveItem();
        return false;
      });
    }

    onMounted(async () => {
      categories.categories = await getTypedCategories();
      await getItem();
    });

    async function removeItem() {
      await saveInterest({
        id: parseInt(item.id, 10),
        delete: true,
      });
      router.push('/manage-content/directory/interests');
    }

    function removeInterestItem() {
      removeItemGetConfirm(removeItem);
    }

    async function handleToggleAddToFav() {
      item.favorite = !item.favorite;
      loading.value = true;
      await saveInterest({
        id: parseInt(item.id, 10),
        favorite: item.favorite,
      });
      loading.value = false;
    }

    return {
      // data
      refForm,
      rules: { interest: requiredRule('Interest'), categoryId: category },
      item,
      loading,
      categories,

      // methods
      validateForm,
      removeInterestItem,
      handleToggleAddToFav,
    };
  },
});
</script>
